/* eslint-disable security/detect-object-injection */
import { Component, Event, type EventEmitter, Element, Host, Listen, Prop, State, h, Watch } from '@stencil/core'

/**
 * TODO: It could be great to render the navigation only on demand. At this time a solution should be found for the animation.
 */

let id = 0
@Component({
  tag: 'cobalt-tabs-horizontal',
  styleUrl: 'cobalt-tabs-horizontal.css'
})
export class CobaltTabsHorizontal {
  tabsId = `tabs-horizontal-${id++}`
  @Element() element: HTMLElement

  /**
   * Set a tab as active by providing the index (tab can not be disabled).
   */
  @Prop({ mutable: true }) activeTabIndex: number = 0
  /**
   * Force the tab labels to have the same size and take the full width of the tabs component.
   */
  @Prop({ mutable: true }) stretch: boolean = false
  /**
   * Change the position of the icon for all tabs.
   */
  @Prop({ mutable: true }) iconPosition: 'top' | 'left' = 'left'
  /**
   * Label of the next button.
   */
  @Prop() nextButtonLabel: string = 'Suivant'
  /**
   * Label of the previous button.
   */
  @Prop() previousButtonLabel: string = 'Précédent'

  @State() internalActiveTabIndex: number = 0
  @State() internalFocusedTabIndex: number = 0
  @State() tabRefs: HTMLElement[] = []
  @State() tabHeaderRefs: HTMLElement[] = []
  @State() tabObjects: any[] = []
  @State() overflow: boolean = false
  @State() isDisplayingButtons: boolean = false

  @Event() cobaltTabChange: EventEmitter

  componentWillLoad(): void {
    if (this.activeTabIndex) this.internalActiveTabIndex = this.activeTabIndex
    if (this.internalActiveTabIndex !== this.activeTabIndex) {
      this.cobaltTabChange.emit({ previousTab: this.internalActiveTabIndex, currentTab: this.activeTabIndex })
    }
    this.internalFocusedTabIndex = this.internalActiveTabIndex
    this.onSlotChange()
  }

  componentDidLoad(): void {
    this.setOverflowState()
    this.isDisplayingButtons = this.overflow && !this.stretch
    this.handleNavigation(this.internalActiveTabIndex)
    // Add keyboard event listeners for each tab header
    this.tabHeaderRefs.forEach((tab, index) => {
      tab.addEventListener('focus', this.onTabFocus(index))
    })

    document.addEventListener('click', event => {
      if (!event.composedPath().includes(this.element)) {
        this.updateTabFocusability()
      }
    })
  }

  disconnectedCallback(): void {
    // Remove keyboard event listeners when component is unmounted
    this.tabHeaderRefs.forEach((tab, index) => {
      tab.removeEventListener('focus', this.onTabFocus(index))
    })
  }

  componentDidUpdate(): void {
    this.updateTabFocusability()
  }

  @Listen('resize', { target: 'window' })
  handleNavigationOnResize(): void {
    this.setOverflowState()
    this.isDisplayingButtons = this.overflow && !this.stretch
    this.handleNavigation(this.internalActiveTabIndex)
  }

  handleNavigation(index: number): void {
    const prevButton = this.element.querySelector('.previous-button')
    const nextButton = this.element.querySelector('.next-button')
    // adjust button animation depending on index
    // the display property is added or removed asynchronously from the width and opacity to allow CSS animation
    if (index > 0 && this.isDisplayingButtons) {
      // @ts-expect-error
      prevButton.style.display = 'block'
      setTimeout(() => {
        prevButton.classList.remove('previous-button--hidden')
      }, 100)
    } else if (index === 0 || !this.isDisplayingButtons) {
      prevButton.classList.add('previous-button--hidden')
      setTimeout(() => {
        // @ts-expect-error
        prevButton.style.display = 'none'
      }, 100)
    }

    if (index < this.tabObjects.length - 1 && this.isDisplayingButtons) {
      // @ts-expect-error
      nextButton.style.display = 'block'
      setTimeout(() => {
        nextButton.classList.remove('next-button--hidden')
      }, 100)
    } else if (index === this.tabObjects.length - 1 || !this.isDisplayingButtons) {
      nextButton.classList.add('next-button--hidden')
      setTimeout(() => {
        // @ts-expect-error
        nextButton.style.display = 'none'
      }, 100)
    }
  }

  setActiveAndFocusedTab(index: number): void {
    this.internalActiveTabIndex = index
    this.internalFocusedTabIndex = index
  }

  @Watch('activeTabIndex')
  activeTabIndexChanged(newValue: number, oldValue: number): void {
    if (newValue !== oldValue) {
      this.setActiveAndFocusedTab(newValue)
    }
  }

  // when a slot is removed / added
  @Listen('slotchange')
  onSlotChange(): void {
    const tabs = this.element.querySelectorAll('cobalt-tab-panel')
    this.tabObjects = Array.from(tabs).map(tab => {
      const pillNumber = tab.getAttribute('notification-pill-number') !== '' ? tab.getAttribute('notification-pill-number') : 'undefined'
      return {
        label: tab.getAttribute('label'),
        iconName: tab.getAttribute('icon-name'),
        notificationPillNumber: pillNumber,
        disabled: tab.getAttribute('disabled') === 'true'
      }
    })
    // Ensure icons have the right variant
    this.setIconDependingOnSelectedTab()

    this.tabRefs = Array.from(tabs) as HTMLElement[]
    this.tabRefs.forEach((tab, index) => {
      tab.setAttribute('slot', `${this.tabsId}_tab-${index}`)
    })
  }

  setIconDependingOnSelectedTab(): void {
    this.tabObjects.forEach((tabPanel, index) => {
      if (!tabPanel.iconName) return
      if (index !== this.internalActiveTabIndex || this.tabObjects[index].disabled) {
        this.tabObjects[index].iconName = tabPanel.iconName.replace('-solid', '-outline')
      } else {
        this.tabObjects[index].iconName = tabPanel.iconName.replace('-outline', '-solid')
      }
    })
  }

  setOverflowState(): void {
    const cobaltTabsList = this.element.querySelector('.cobalt-tabs-list')
    if (!cobaltTabsList) return
    this.overflow = cobaltTabsList.scrollWidth > cobaltTabsList.clientWidth
  }

  onTabFocus(index) {
    return () => {
      this.internalFocusedTabIndex = index
    }
  }

  private updateTabFocusability(): void {
    this.tabHeaderRefs.forEach((tab, index) => {
      tab.tabIndex = index === this.internalActiveTabIndex ? 0 : -1
    })
  }

  private focusNextTab(): void {
    let nextIndex = this.internalFocusedTabIndex + 1
    while (nextIndex < this.tabHeaderRefs.length && this.tabObjects[nextIndex].disabled) {
      nextIndex++
    }
    if (nextIndex >= 0 && nextIndex < this.tabHeaderRefs.length) {
      this.internalFocusedTabIndex = nextIndex
      this.tabHeaderRefs[nextIndex].focus()
      this.tabHeaderRefs[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
    this.handleNavigation(nextIndex)
  }

  private focusPreviousTab(): void {
    let prevIndex = this.internalFocusedTabIndex - 1
    while (prevIndex >= 0 && this.tabObjects[prevIndex].disabled) {
      prevIndex--
    }
    if (prevIndex >= 0 && prevIndex < this.tabHeaderRefs.length) {
      this.internalFocusedTabIndex = prevIndex
      this.tabHeaderRefs[prevIndex].focus()
      this.tabHeaderRefs[prevIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
    this.handleNavigation(prevIndex)
  }

  private getTabItemClass(index: number): string {
    const isActive = index === this.internalActiveTabIndex && !this.tabObjects[index].disabled
    const isDisabled = this.tabObjects[index].disabled
    return `cobalt-tab-item ${isActive ? 'tab-item--selected' : ''} ${isDisabled ? 'tab-item--disabled' : ''} ${this.stretch ? 'tab-item--stretch' : ''} tab-item--icon-${
      this.iconPosition
    }`
  }

  private handleClick(tab, index): void {
    if (tab.disabled) return
    this.cobaltTabChange.emit({ previousTab: this.internalActiveTabIndex, currentTab: index })
    this.internalActiveTabIndex = index
    this.internalFocusedTabIndex = index
    // Ensure to get back to original icon
    this.setIconDependingOnSelectedTab()
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        // Shift + Tab
        if (this.internalFocusedTabIndex === 0) {
          // Allow default behavior to move focus out of component
        } else {
          event.preventDefault()
          this.focusPreviousTab()
        }
      } else {
        // Tab
        if (this.internalFocusedTabIndex === this.tabHeaderRefs.length - 1) {
          // Allow default behavior to move focus out of component
        } else {
          event.preventDefault()
          this.focusNextTab()
        }
      }
    } else if (event.key === 'Enter' && this.internalFocusedTabIndex !== -1 && !this.tabObjects[this.internalFocusedTabIndex].disabled) {
      const previouslyActiveTabIndex = this.internalActiveTabIndex
      this.cobaltTabChange.emit({ previousTab: previouslyActiveTabIndex, currentTab: this.internalFocusedTabIndex })
      this.internalActiveTabIndex = this.internalFocusedTabIndex
      // Ensure to get back to original icon
      this.setIconDependingOnSelectedTab()
    }
  }

  render(): any {
    return (
      <Host>
        <div id={this.tabsId} class="cobalt-tabs cobalt-tabs--horizontal">
          <div class="list-header-wrapper">
            <cobalt-icon-button
              class="previous-button previous-button--hidden"
              label={this.previousButtonLabel}
              iconName="chevron_left-outline"
              variant="quaternary"
              onCobaltIconButtonClick={() => {
                this.focusPreviousTab()
              }}
              aria-hidden={!this.isDisplayingButtons}
            ></cobalt-icon-button>
            <div class="cobalt-tabs-list-header">
              <ul role="tablist" class={`cobalt-tabs-list ${this.iconPosition === 'top' ? 'typography-monoline-xsmall' : 'typography-monoline-medium'}`}>
                {this.tabObjects.map((tab, index) => (
                  <li
                    id={`tab-label-${index}`}
                    class={this.getTabItemClass(index)}
                    ref={el => (this.tabHeaderRefs[index] = el)}
                    tabindex="0"
                    onClick={() => {
                      this.handleClick(tab, index)
                    }}
                    aria-selected={index === this.internalActiveTabIndex ? 'true' : 'false'}
                    aria-disabled={tab.disabled ? 'true' : 'false'}
                    role="tab"
                    aria-controls={`${this.tabsId}_tab-panel-${index}`}
                  >
                    <div class="tab-label-content">
                      {tab.iconName && (
                        <div class="icon-box">
                          {tab.notificationPillNumber && typeof tab.notificationPillNumber !== 'undefined' && this.iconPosition === 'top' && (
                            <cobalt-notification-pill number={tab.notificationPillNumber}></cobalt-notification-pill>
                          )}
                          <cobalt-icon name={tab.iconName} size={this.iconPosition === 'top' ? 'number-size-icon-large' : 'number-size-icon-medium'}></cobalt-icon>
                        </div>
                      )}
                      <span class="tab-label">{tab.label}</span>
                      {tab.notificationPillNumber && typeof tab.notificationPillNumber !== 'undefined' && this.iconPosition === 'left' && (
                        <cobalt-notification-pill number={tab.notificationPillNumber}></cobalt-notification-pill>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <cobalt-icon-button
              class="next-button next-button--hidden"
              label={this.nextButtonLabel}
              iconName="chevron_right-outline"
              variant="quaternary"
              onCobaltIconButtonClick={() => {
                this.focusNextTab()
              }}
              aria-hidden={!this.isDisplayingButtons}
            ></cobalt-icon-button>
          </div>
          <div class="tab-content">
            {Array.from(this.tabObjects).map((_, index) => (
              <div
                id={`${this.tabsId}_tab-panel-${index}`}
                role="tabpanel"
                aria-labelledby={`tab-label-${index}`}
                style={{ display: index === this.internalActiveTabIndex && !this.tabObjects[index].disabled ? 'block' : 'none' }}
              >
                <slot name={`${this.tabsId}_tab-${index}`} />
              </div>
            ))}
          </div>
        </div>
      </Host>
    )
  }
}
